import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Divisions = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Divisions", "create", false);
  const canEdit = hasRight(permissionMap, "Divisions", "update", false);
  const canDelete = hasRight(permissionMap, "Divisions", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/divisions');
        setDivisions(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDivisions();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...isMobile ? { width: 60 } : { flex: 1 },
    },
    {
      field: "divisionCode",
      headerName: "DivisionCode",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
    },
    {
      field: "description",
      headerName: "Description",
      ...isMobile ? { width: 300 } : { flex: 5 },
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      align: "center",
      ...isMobile ? { width: 80 } : { flex: 1 },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
  ], [isMobile]);
  
  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/divisions/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);                  
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate,
    canEdit,
    canDelete,
  ]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, divisions);
    if (selectedIds.length === 0) { toast.error('No rows selected for deletion!'); return; }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/divisions/delete/${idToDelete}`);
        setDivisions(divisions.filter((item) => item._id !== idToDelete));
        toast.success('Division deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, divisions);
        if (selectedIds.length === 0) { toast.error('No rows selected for deletion!'); return; }
        const res = await api.patch('/divisions/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setDivisions(divisions.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete divisions!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, divisions]);

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Divisions" 
          subtitle={loading ? 'Loading...' : 'List of Divisions'} 
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton 
                    disabled={!isAnyRowSelected(rowSelectionModel, divisions)} 
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/divisions/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">Error: {error?.message || ''}</Typography></FlexCenter>
      ) : (
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={divisions}
                primaryField="divisionCode"
                fields={[
                  { label: "Description", key: "description" },
                  { label: "Sort Order", key: "sortOrder" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/divisions/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={divisions || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                  sx: { "& .MuiDataGrid-row.isActive-false": { backgroundColor: `${theme.palette.primary.light} !important` } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)} 
        onConfirm={executeDelete} 
        deleteType={deleteType} 
      />
    </Box>
  );
};

export default Divisions;
